import { faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faCircleInfo, faRedo, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from "react";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import StepNav from "../../../../components/StepNav";
import moment from "moment";

const CounsellingForm = () => {
    const navigate = useNavigate();
    const axios = useAxiosPrivate();
    const { createInfo, auth, userPolicy, setShowPaymentRedirect } = useAuth();

    const [dayDelay] = useState(7);

    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [curStep, setCurStep] = useState(1)
    const [fetchingAvailability, setFetchingAvailability] = useState(false)
    const initialFetchDone = useRef(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [slotSelected, setSlotSelected] = useState(null)
    const [selectedDay, setSelectedDay] = useState(moment().add(dayDelay, 'days'))
    const [openSection, setOpenSection] = useState(null)
    const [reason, setReason] = useState('')

    const [allSlots, setAllSlots] = useState([])

    const morningSlots = allSlots.filter((slot) => {
        // Parse UTC time, convert to local time
        const time = moment(slot.time)
        const hour = time.hour()
        return hour >= 8 && hour < 12 && selectedDay.isSame(time, 'day')
    })

    const afternoonSlots = allSlots.filter((slot) => {
        const time = moment(slot.time)
        const hour = time.hour()        
        return hour >= 12 && hour < 17 && selectedDay.isSame(time, 'day')
    })

    const eveningSlots = allSlots.filter((slot) => {
        const time = moment(slot.time)
        const hour = time.hour()        
        return hour >= 17 && hour < 22 && selectedDay.isSame(time, 'day')
    })

    const fetchAvailability = async () => {
        try {
            if (fetchingAvailability) {
                return
            }
            setFetchingAvailability(true)
            const start = moment().add(dayDelay, 'days').format('YYYY-MM-DD')
            const end = moment().add(dayDelay+2, 'days').format('YYYY-MM-DD')

            const response = await axios.post(process.env.REACT_APP_WBA_API_URL + '/availabilities/8/'+start+'/'+end+'?api_token=' + process.env.REACT_APP_WBA_API_KEY)
            if (!response.data.error) {
                setAllSlots(response.data.result)
            } else {
                createInfo('error', 'Failed to fetch availabilities')
            }
        } catch (error) {
            createInfo('error', 'Failed to fetch availabilities')
        } finally {
            setFetchingAvailability(false)
        }
    }
    const location = useLocation()
    const product = location?.state?.product
    const [hasAccess, setHasAccess] = useState(true)
    const [accessMode, setAccessMode] = useState('fallback') // 'api' | 'fallback'
    const [accessByProduct, setAccessByProduct] = useState({})

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            name: auth?.user?.name || "",
            email: auth?.user?.email || "",
            dob: auth?.user?.date_of_birth || "",
            contact: auth?.user?.phone_number || "",
        }));
    }, [auth?.user]);


    useEffect(() => {
        setHasAccess(checkProductAccess(product?.title))
    }, [])

    useEffect(() => {
        if (!auth?.token) return
        let cancelled = false
        setAccessMode('fallback')
        axios
            .post('/service-access/check', {
                product_titles: [product.title],
                ...(process.env.REACT_APP_ENV !== 'production'
                    ? {
                        policy_override: {
                            product: userPolicy?.product,
                            status: userPolicy?.status,
                            department: userPolicy?.department,
                        },
                    }
                    : {}),
            })
            .then((response) => {
                if (cancelled) return
                const byProduct = response?.data?.result?.by_product
                if (byProduct && typeof byProduct === 'object') {
                    setAccessByProduct(byProduct)
                    setAccessMode('api')
                }
            })
            .catch(() => {
                if (cancelled) return
                setAccessMode('fallback')
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.token, userPolicy?.id])

    // Process payment - placeholder function
    const processPayment = (product, enquriy) => {
        if (!!product?.paymentLink) {
            // Append the parameter to the URL
            const finalUrl = `${product.paymentLink}?client_reference_id=${enquriy}`;

            window.location.href = finalUrl;
        } else {
            createInfo('error', 'Payment link not available.');
        }
    }

    // Check overall service access based on user policy
    const checkOverallServiceAccess = () => {
        return !(
            ['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.product) ||
            !['live', 'expiring'].includes(userPolicy?.status)
        )
    }

    // Check access for specific products
    const checkProductAccess = (productTitle) => {
        console.log(accessMode)
        if (accessMode === 'api') {
            return !!accessByProduct?.[productTitle]
        }

        // First check overall access
        if (!checkOverallServiceAccess()) return false

        if (['SAS', 'Mutual'].includes(userPolicy?.department)) return true
        if (['SWB OH+', 'SWB OH+ Only'].includes(userPolicy?.department)) return false

        if (![
            'Counselling',
            'Mindfulness',
            'Relaxation Room Live',
            'Nurse Support',
            'Menopause Support',
            'GP Services',
            'SAS Gym',
            'Happier Healthier You',
            'HHY Community',
            'Physiotherapy',
            'Preparing Parents',
            'Food Intolerance Testing',
            'Financial Wellbeing',
            'Manspace',
            'Pupil Yoga',
            'Pastoral Resources',
            'Awards & Programmes',
            'Ask the PT'
        ].includes(productTitle)) return false

        switch (productTitle) {
            case 'Counselling':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'GP Services':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Happier Healthier You':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Physiotherapy':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Preparing Parents':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            case 'Pupil Counselling':
                if (userPolicy?.department === 'AIS' || userPolicy?.product === 'SWB Essential') return false
                return true
            default:
                return true
        }
    }

    const goBack = () => {
        if (curStep === 1) {
            navigate(-1)
        } else {
            setCurStep(curStep - 1)
        }
    }

    const handleChange = (e, fieldName) => {
        const value =
            e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
    };

    const resolvedName = formData.name || auth?.user?.name || '';
    const resolvedEmail = formData.email ?? auth?.user?.email ?? '';
    const resolvedDob = formData.dob || auth?.user?.date_of_birth || '';
    const resolvedSchoolName = formData.schoolName || auth?.user?.client?.organisation?.name || '';
    const resolvedSchoolPostcode = formData.schoolPostcode || auth?.user?.client?.organisation?.postcode || '';

    const formattedDob = (() => {
        const rawDob = resolvedDob;
        const [year, month, day] = String(rawDob).split("-");
        if (year && month && day) {
            return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year).padStart(4, "0")}`;
        }
        return rawDob;
    })();

    const validateStep = () => {
        switch (curStep) {
            case 1:
                return true
            case 2:
                return resolvedName &&
                    resolvedName.trim() !== "" &&
                    resolvedEmail &&
                    resolvedEmail.trim() !== "" &&
                    resolvedDob &&
                    resolvedDob.trim() !== "" &&
                    formData.contact &&
                    formData.contact.trim() !== "" &&
                    formData.confirm;
            case 3:
                return slotSelected !== null
            case 4:
                return slotSelected !== null && reason.trim()
            default:
                return false
        }
    }


    const StepOne = () => {
        return (
            <>
                <div className="border border-blue-400 bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3">
                    <FontAwesomeIcon icon={faCircleInfo} className="mt-1 text-lg" />
                    <p className="text-sm">
                        You must not use this service for emergencies or urgent
                        conditions, as this may delay necessary treatment.
                    </p>
                </div>

                <div className="text-[#41264B] text-lg font-semibold mt-4">
                    How it works
                </div>
                <p className="mt-2 text-[#41264B] whitespace-pre-line">
                    Our BACP registered counsellors provide confidential, individual
                    therapy sessions which are delivered safely and appropriately
                    and available whether you are still working in school or absent
                    through illness. The need for therapy does not need to be work
                    related.
                </p>
            </>
        )
    }

    const StepTwo = () => {
        return (
            <>
                <p className="mt-2 text-[#41264B] whitespace-pre-line text-center">
                    Complete the form below to make your request for Counselling. All requests are confidential.
                </p>
                <form className="space-y-4 text-[#41264B] p-4 rounded-md mt-6">
                    {/* Name Field - Read Only */}
                    <div>
                        <label className="block text-sm/6 font-semibold">Name</label>
                        <p className="mt-1 text-base text-gray-900 sm:text-sm/6">{formData.name || ''}</p>
                    </div>

                    {/* Date of Birth Field - Read Only */}
                    <div>
                        <label className="block text-sm/6 font-semibold">
                            Date Of Birth
                        </label>
                        <p className="mt-1 text-base text-gray-900 sm:text-sm/6">
                            {formattedDob}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Preferred Contact Email for Appointments<span className="text-red-600">*</span></label>
                        <input
                            type="email"
                            className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                            placeholder="Email Address"
                            value={formData.email || ""}
                            onChange={(e) => handleChange(e, "email")}
                        />
                    </div>

                    {/* Contact Number Field */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Contact Number <span className="text-red-600">*</span>
                        </label>
                        <input
                            type="text"
                            className="w-full p-2 rounded text-[#41264B] border shadow-sm"
                            placeholder="Contact Number"
                            value={formData.contact || ""}
                            onChange={(e) => handleChange(e, "contact")}
                        />
                    </div>

                    {/* Still In Work Checkbox */}
                    <div className="flex items-center space-x-2">
                        <input
                            name="stillInWork"
                            id="stillInWork"
                            type="checkbox"
                            checked={!!formData.stillInWork}
                            onChange={(e) => handleChange(e, "stillInWork")}
                        />
                        <label htmlFor="stillInWork" className="text-sm">
                            Please tick if you are still in work
                        </label>
                    </div>

                    {/* Confirmation Checkbox */}
                    <div className="flex items-center space-x-2">
                        <input
                            name="confirm"
                            id="confirm"
                            type="checkbox"
                            checked={!!formData.confirm}
                            onChange={(e) => handleChange(e, "confirm")}
                        />
                        <label htmlFor="confirm" className="text-sm">
                            By Requesting a service, I am giving my permission for
                            School Wellbeing and third-party service providers to
                            contact me. For more information on how we collect and
                            manage your data, please don't hesitate to contact School
                            Wellbeing on 01773814404 <span className="text-red-600">*</span>
                        </label>
                    </div>
                    {!validateStep() && Object.keys(formData).length > 0 && (
                        <p className="text-sm text-red-600">
                            Please fill in all required fields before submitting.
                        </p>
                    )}
                </form>
            </>
        )
    }

    const StepThree = () => {        
        return (
            <div className="p-4 flex-1">
                <div className="text-center font-semibold mb-4">
                    Please select an appointment slot from the list below
                </div>

                <div className="flex my-5 justify-center gap-2">
                    <div className="font-semibold">
                        Slot Selected:
                    </div>
                    {slotSelected?.time ? (
                        <div className="">
                            {moment(slotSelected?.time).format('dddd Do MMMM HH:mm') ?? ''}
                        </div>
                    ) : (
                        <div className="">
                            None
                        </div>
                    )}
                </div>

                <div className="flex justify-center">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {[moment().add(dayDelay, 'days'),moment().add(dayDelay+1, 'days'),moment().add(dayDelay+2, 'days')].map((item, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => setSelectedDay(item)}
                                className={`border rounded-lg py-2 font-medium transition px-10
                                    ${selectedDay.isSame(item, 'day')
                                        ? 'bg-light-purple border-light-purple'
                                        : 'bg-white'
                                    }`}
                                    >
                                {item.format('ddd DD MMM')}
                            </button>
                        ))}
                    </div>
                </div>
                
                {errorMsg && (
                    <div className="text-center mt-5 text-[#ff0000]">
                        {errorMsg}
                    </div>
                )}        
                
                <div className="flex-col mt-5 space-y-6">
                    {[{slots: morningSlots, section: 'Morning'}, {slots: afternoonSlots, section: 'Afternoon'}, {slots: eveningSlots, section: 'Evening'}].map(({slots, section}) => {
                        return (
                            <div className="border rounded-lg overflow-hidden">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setOpenSection(openSection === section ? '' : section)
                                    }}
                                    className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100"
                                >
                                    <span className="font-medium">
                                        {section} {fetchingAvailability
                                            ? <FontAwesomeIcon icon={faSpinner} className="ml-2 animate-spin" />
                                            : `(${slots.length} available)`}
                                    </span>

                                    <FontAwesomeIcon
                                        icon={openSection === section ? faChevronUp : faChevronDown}
                                        className="text-gray-500"
                                    />
                                </button>

                                {openSection === section && (
                                    <div className="p-4 bg-white">
                                        <div className="grid grid-cols-3 gap-2">
                                            {!fetchingAvailability && slots.map((slot, index) => (
                                                <button
                                                    type="button"
                                                    key={index}
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        setSlotSelected(slot)
                                                    }}
                                                    className={`rounded-md py-2 px-3 text-sm font-medium border ${
                                                        slotSelected === slot
                                                            ? 'bg-light-purple border-light-purple text-white'
                                                            : 'bg-white hover:bg-gray-50'
                                                    }`}
                                                >
                                                    {moment(slot.time).format('HH:mm')}
                                                </button>
                                            ))}
                                            {fetchingAvailability && (
                                                <div>
                                                    Loading...
                                                </div>
                                            )}
                                            {!fetchingAvailability && slots?.length === 0 && (
                                                <div>No Slots Available</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        );
    };  

    const handleNextStep = async () => {
        switch (curStep) {
            case 1:
            case 2:
                if (!fetchingAvailability && !initialFetchDone.current) {
                    fetchAvailability() 
                    initialFetchDone.current = true
                }
                setCurStep(curStep + 1)
                break
            case 3:
                setSubmitting(true);

                try {
                    const infoObj = {
                        user: {
                            name: resolvedName,
                            email: resolvedEmail,
                            phone_number: formData.contact || "",
                            dob: resolvedDob,
                        },
                        school: {
                            schoolName: resolvedSchoolName,
                            schoolPostcode: resolvedSchoolPostcode,
                        },
                        contact: formData.bestTime || "",
                        extras: "",
                    };
    
                    // Handle specific fields like stillInWork
                    if (formData.stillInWork) {
                        infoObj.extras = "This staff member is currently working.";
                    }
    
                    const data = {
                        info: JSON.stringify(infoObj),
                        name: 'Mental Health',
                        product: "counselling",
                        type: 'staff',
                        staff_id: auth?.user?.client?.staff_id,
                        policy_id: userPolicy?.id,
                        policy_reference: userPolicy?.reference,
                        user_id: auth?.user?.id,
                        client_id: auth?.user?.client_id,
                        ...slotSelected
                    }
    
                    const response = await axios.post('/tasks/create-from-enquiry', data)
    
                    if (response.data.error) {
                        switch (response.data.message) {
                            case 'Ongoing Enquiry Exists':
                                setErrorMsg("Enquiry Already Exists");
                                break
                            case 'Slot unavailable':    
                                setErrorMsg(response.data.message);
                                break
                            case 'Failed to reserve slot':
                            default:
                                setErrorMsg('Failed to create request');
                                break
                        }
                    } else if (!response.data.error) {
                        setErrorMsg('')
                        createInfo('success', "Submitted Successfully.");
                    } else {
                        setErrorMsg('Failed to create request')
                    }
                } catch (error) {
                    console.error(error)
                    setErrorMsg('Failed to create request')
                } finally {
                    setSubmitting(false)
                }
                break
            default:
                break
        }
    }

    return (
        <section>
            <div className="max-w-4xl mx-auto p-6 space-y-8 text-portal-dark-blue">
                <StepNav currentStep={curStep} totalSteps={3} stepHeaders={['Counselling', 'Counselling - Contact Details', 'Counselling - Select a Time']}/>
                <section className="pb-6">
                    {curStep === 1 ? (
                        <StepOne />
                    ) : (curStep === 2) ? (
                        <StepTwo />
                    ) :  (
                        <StepThree />
                    )}
                    <div className="flex items-center justify-between space-x-4">
                        <button
                            type="submit"
                            disabled={!validateStep() || submitting}
                            onClick={() => handleNextStep()}
                            className={`w-full py-2 rounded font-semibold mt-4 border-2 transition-all ${!validateStep()
                                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                                : "bg-white text-[#41264B] hover:shadow-lg hover:border-[#41264B] hover:bg-[#e6e3ea]"
                                } ${submitting && 'cursor-not-allowed'}`}
                        >
                            {curStep === 1 ? 'Request Help' : (curStep === 3) ? 'Submit Request' : 'Continue'}{submitting && <FontAwesomeIcon icon={faSpinner} className="ml-2 animate-spin" />}
                        </button>
                        <button
                            type="button"
                            onClick={goBack}
                            disabled={submitting}
                            className={`w-full bg-gray-300 text-gray-800 py-2 rounded font-semibold mt-4 hover:bg-white border-2 border-gray-300 ${submitting && 'cursor-not-allowed'}`}
                        >
                            {curStep === 1 ? 'Cancel' : 'Back'}
                        </button>
                    </div>
                </section>

            </div>
        </section>
    );
};

export default CounsellingForm;









